import React, { useEffect, useMemo, useState } from 'react';
import DashboardHeader from "./layouts/dashboard_header";
import SideMenu from "./layouts/dashboard_menu";
import DashboardFooter from "./layouts/dashboard_footer";
import { buildApiUrl, getStaffDetails, useBaseUrl } from "../utils/utils";

type InboxTab = 'all' | 'notifications' | 'alerts' | 'messages';

interface InboxItem {
    inbox_id: string;
    item_type: 'notification' | 'message' | 'alert';
    priority: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    body: string;
    created_at: string;
    is_read: boolean;
    sender_name?: string;
    action_url?: string;
}

interface InboxCounts {
    all: number;
    messages: number;
    alerts: number;
    notifications: number;
    unread_total: number;
    unread_messages: number;
    unread_alerts: number;
    unread_notifications: number;
}

interface PaginationState {
    current_page: number;
    total_pages: number;
    total_items: number;
    has_next: boolean;
    has_previous: boolean;
}

const emptyCounts: InboxCounts = {
    all: 0,
    messages: 0,
    alerts: 0,
    notifications: 0,
    unread_total: 0,
    unread_messages: 0,
    unread_alerts: 0,
    unread_notifications: 0,
};

const emptyPagination: PaginationState = {
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    has_next: false,
    has_previous: false,
};

const Notifications: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [clinicName, setClinicName] = useState<string>("Facility");
    const [staffId, setStaffId] = useState<number | null>(null);
    const [institutionId, setInstitutionId] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<InboxTab>('all');
    const [search, setSearch] = useState<string>('');
    const [priority, setPriority] = useState<string>('all');
    const [readStatus, setReadStatus] = useState<string>('all');
    const [counts, setCounts] = useState<InboxCounts>(emptyCounts);
    const [items, setItems] = useState<InboxItem[]>([]);
    const [pagination, setPagination] = useState<PaginationState>(emptyPagination);
    const [loading, setLoading] = useState<boolean>(true);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        document.title = "Notifications - Uzima Healthcare";

        const LoadPageContext = async () => {
            const staffDetails: any = await getStaffDetails();
            setClinicName(staffDetails?.clinic_name || staffDetails?.institution || "Facility");
            setStaffId(staffDetails?.staff_id || null);
            setInstitutionId(staffDetails?.institution_id || null);
        };

        LoadPageContext();
    }, []);

    const activeTabParam = useMemo(() => activeTab === 'all' ? 'all' : activeTab, [activeTab]);

    const BuildQueryString = (pageNumber: number) => {
        const params = new URLSearchParams({
            staff_id: String(staffId),
            institution_id: String(institutionId),
            tab: activeTabParam,
            page: String(pageNumber),
            page_size: '12'
        });

        if (search.trim()) {
            params.set('search', search.trim());
        }

        if (priority !== 'all') {
            params.set('priority', priority);
        }

        if (readStatus !== 'all') {
            params.set('read_status', readStatus);
        }

        return params.toString();
    };

    const LoadCounts = async () => {
        const response = await fetch(buildApiUrl(baseUrl, `communications/api/inbox/summary/?staff_id=${staffId}&institution_id=${institutionId}`), {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });

        if (!response.ok) {
            throw new Error('Failed to load notification counts');
        }

        const payload = await response.json();
        setCounts(payload?.data?.counts || emptyCounts);
    };

    const LoadItems = async (pageNumber: number = 1) => {
        const response = await fetch(buildApiUrl(baseUrl, `communications/api/inbox/items/?${BuildQueryString(pageNumber)}`), {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });

        if (!response.ok) {
            throw new Error('Failed to load notifications');
        }

        const payload = await response.json();
        setItems(Array.isArray(payload?.data?.items) ? payload.data.items : []);
        setPagination(payload?.data?.pagination || emptyPagination);
    };

    const RefreshInbox = async (pageNumber: number = 1, showRefreshing: boolean = false) => {
        if (!staffId || !institutionId) {
            return;
        }

        try {
            if (showRefreshing) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            setErrorMessage('');
            await Promise.all([
                LoadCounts(),
                LoadItems(pageNumber)
            ]);
        } catch (error) {
            console.error('Notifications load error:', error);
            setErrorMessage('Unable to load notifications right now.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        if (!staffId || !institutionId) {
            return;
        }

        RefreshInbox(1);

        const refreshTimer = window.setInterval(() => {
            RefreshInbox(pagination.current_page || 1, true);
        }, 12000);

        const RefreshInboxListener = () => {
            RefreshInbox(pagination.current_page || 1, true);
        };

        window.addEventListener('uzima:inbox-updated', RefreshInboxListener);

        return () => {
            window.clearInterval(refreshTimer);
            window.removeEventListener('uzima:inbox-updated', RefreshInboxListener);
        };
    }, [activeTab, institutionId, pagination.current_page, staffId]);

    useEffect(() => {
        if (!staffId || !institutionId) {
            return;
        }

        const debounceTimer = window.setTimeout(() => {
            RefreshInbox(1);
        }, 350);

        return () => window.clearTimeout(debounceTimer);
    }, [search, priority, readStatus]);

    const MarkOneRead = async (inboxId: string) => {
        if (!staffId || !institutionId) {
            return;
        }

        try {
            await fetch(buildApiUrl(baseUrl, `communications/api/inbox/${inboxId}/read/`), {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    staff_id: staffId,
                    institution_id: institutionId
                })
            });
            await RefreshInbox(pagination.current_page, true);
        } catch (error) {
            console.error('Mark notification read error:', error);
        }
    };

    const MarkAllRead = async () => {
        if (!staffId || !institutionId) {
            return;
        }

        try {
            await fetch(buildApiUrl(baseUrl, 'communications/api/inbox/mark-all-read/'), {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    staff_id: staffId,
                    institution_id: institutionId
                })
            });
            await RefreshInbox(1, true);
        } catch (error) {
            console.error('Mark all notifications read error:', error);
        }
    };

    const FormatRelativeTime = (timestamp: string) => {
        const diffMinutes = Math.max(1, Math.floor((Date.now() - new Date(timestamp).getTime()) / 60000));
        if (diffMinutes < 60) {
            return `${diffMinutes} min ago`;
        }
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) {
            return `${diffHours} hr ago`;
        }
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    };

    const OpenItemAction = (item: InboxItem) => {
        if (item.action_url) {
            window.location.href = item.action_url;
        }
    };

    const unreadCountForActiveTab = activeTab === 'messages'
        ? counts.unread_messages
        : activeTab === 'alerts'
            ? counts.unread_alerts
            : activeTab === 'notifications'
                ? counts.unread_notifications
                : counts.unread_total;

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className={"vertical-overlay"} />

            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="row mt-2">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0">{clinicName} - Notifications</h4>
                                    <div className="page-title-right">
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><span>Dashboards</span></li>
                                            <li className="breadcrumb-item active">Notifications</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mt-4 g-3">
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body text-center">
                                        <h3 className="mb-1">{counts.all}</h3>
                                        <p className="text-muted mb-0">Total items</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body text-center">
                                        <h3 className="mb-1 text-warning">{counts.unread_total}</h3>
                                        <p className="text-muted mb-0">Unread</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body text-center">
                                        <h3 className="mb-1 text-danger">{counts.alerts}</h3>
                                        <p className="text-muted mb-0">Alerts</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body text-center">
                                        <h3 className="mb-1 text-primary">{counts.messages}</h3>
                                        <p className="text-muted mb-0">Messages</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mt-4">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
                                        <div>
                                            <h5 className="card-title mb-1">Communication inbox</h5>
                                            <p className="text-muted mb-0">Live notifications, alerts, and messages fetched through XHR.</p>
                                        </div>
                                        <div className="d-flex gap-2 flex-wrap">
                                            <button type="button" className="btn btn-outline-primary btn-sm" disabled={refreshing} onClick={() => RefreshInbox(1, true)}>
                                                {refreshing ? 'Refreshing...' : 'Refresh'}
                                            </button>
                                            {counts.unread_total > 0 ? (
                                                <button type="button" className="btn btn-primary btn-sm" onClick={MarkAllRead}>
                                                    Mark all as read
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="d-flex flex-wrap gap-2 mb-3">
                                            <button type="button" className={`btn btn-sm ${activeTab === 'all' ? 'btn-primary' : 'btn-soft-primary'}`} onClick={() => setActiveTab('all')}>All ({counts.all})</button>
                                            <button type="button" className={`btn btn-sm ${activeTab === 'notifications' ? 'btn-success' : 'btn-soft-success'}`} onClick={() => setActiveTab('notifications')}>Notifications ({counts.notifications})</button>
                                            <button type="button" className={`btn btn-sm ${activeTab === 'alerts' ? 'btn-danger' : 'btn-soft-danger'}`} onClick={() => setActiveTab('alerts')}>Alerts ({counts.alerts})</button>
                                            <button type="button" className={`btn btn-sm ${activeTab === 'messages' ? 'btn-info' : 'btn-soft-info'}`} onClick={() => setActiveTab('messages')}>Messages ({counts.messages})</button>
                                        </div>

                                        <div className="row g-3 mb-3">
                                            <div className="col-lg-5">
                                                <input className="form-control" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search title or content" />
                                            </div>
                                            <div className="col-lg-3">
                                                <select className="form-select" value={priority} onChange={(event) => setPriority(event.target.value)}>
                                                    <option value="all">All priorities</option>
                                                    <option value="low">Low</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="high">High</option>
                                                    <option value="critical">Critical</option>
                                                </select>
                                            </div>
                                            <div className="col-lg-4">
                                                <select className="form-select" value={readStatus} onChange={(event) => setReadStatus(event.target.value)}>
                                                    <option value="all">All statuses</option>
                                                    <option value="unread">Unread only</option>
                                                    <option value="read">Read only</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <span className="badge bg-warning text-dark">{unreadCountForActiveTab} unread in this view</span>
                                        </div>

                                        {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}

                                        {loading ? (
                                            <div className="text-center py-5 text-muted">Loading notifications...</div>
                                        ) : items.length === 0 ? (
                                            <div className="alert alert-info mb-0">No items matched your selected filters.</div>
                                        ) : (
                                            <div className="d-flex flex-column gap-3">
                                                {items.map((item) => (
                                                    <div key={item.inbox_id} className={`border rounded p-3 ${item.is_read ? 'bg-light' : 'bg-white border-primary-subtle'}`}>
                                                        <div className="d-flex justify-content-between flex-wrap gap-3">
                                                            <div>
                                                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                                                    <h6 className="mb-0">{item.title}</h6>
                                                                    <span className={`badge ${item.item_type === 'alert' ? 'bg-danger' : item.item_type === 'message' ? 'bg-info' : 'bg-success'}`}>{item.item_type}</span>
                                                                    <span className="badge bg-dark-subtle text-dark">{item.priority}</span>
                                                                    {!item.is_read ? <span className="badge bg-warning text-dark">Unread</span> : null}
                                                                </div>
                                                                <p className="text-muted mb-2 mt-2">{item.body}</p>
                                                                <small className="text-muted">{item.sender_name || 'System'} • {FormatRelativeTime(item.created_at)}</small>
                                                            </div>
                                                            <div className="d-flex gap-2 flex-wrap align-items-start">
                                                                {!item.is_read ? (
                                                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => MarkOneRead(item.inbox_id)}>
                                                                        Mark read
                                                                    </button>
                                                                ) : null}
                                                                {item.action_url ? (
                                                                    <button type="button" className="btn btn-sm btn-soft-secondary" onClick={() => OpenItemAction(item)}>
                                                                        Open
                                                                    </button>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-footer d-flex justify-content-between align-items-center flex-wrap gap-3">
                                        <span className="text-muted">Showing page {pagination.current_page} of {pagination.total_pages} • {pagination.total_items} total items</span>
                                        <div className="btn-group">
                                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={!pagination.has_previous || loading} onClick={() => RefreshInbox(pagination.current_page - 1, true)}>Previous</button>
                                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={!pagination.has_next || loading} onClick={() => RefreshInbox(pagination.current_page + 1, true)}>Next</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <DashboardFooter />
                </div>
            </div>

            <canvas />
        </>
    );
};

export default Notifications;